import React, { useRef } from 'react';
import type { AppView } from '../../types';

/**
 * Umschalter zwischen Geschwister-Ansichten eines Menüpunkts (Menü-Umbau
 * 19.10.2026): „Karte" führt auf Projekte- und Kundenkarte, „Auswertungen"
 * auf KPI & Cockpit und Steuerung. Die Seitenleiste hat je nur EINEN Eintrag;
 * welche Ansicht darunter liegt, wählt man hier oben.
 *
 * Optik wie die Bereichswahl im ErpCockpit, damit beide Ebenen gleich lesen.
 * Welche Einträge jemand sieht (Rechte, Handy-Umfang), entscheidet App.tsx —
 * hier wird nur gezeichnet. App.tsx rendert den Umschalter in einer Hülle, die
 * beim Wechsel stehen bleibt; der Fokus bleibt so auf dem Knopf.
 */

export interface UmschalterEintrag {
    view: AppView;
    label: string;
    icon?: React.ReactNode;
}

interface Props {
    eintraege: readonly UmschalterEintrag[];
    aktiv: AppView;
    onWechsel: (view: AppView) => void;
}

export const BereichsUmschalter: React.FC<Props> = ({ eintraege, aktiv, onWechsel }) => {
    const knoepfe = useRef<(HTMLButtonElement | null)[]>([]);

    // Ein einzelner Eintrag ist keine Wahl — dann gar keine Leiste, auch kein
    // leerer Rand über der Ansicht.
    if (eintraege.length < 2) return null;

    const aktivIndex = eintraege.findIndex(e => e.view === aktiv);
    // Roving tabindex (Tab-Muster): genau ein Knopf ist per Tab erreichbar,
    // die Pfeiltasten wandern darin. Fehlt die aktive Ansicht, der erste.
    const tabStopp = aktivIndex >= 0 ? aktivIndex : 0;

    const wechsle = (view: AppView) => {
        if (view !== aktiv) onWechsel(view);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let ziel: number | null = null;
        if (e.key === 'ArrowRight') ziel = (index + 1) % eintraege.length;
        else if (e.key === 'ArrowLeft') ziel = (index - 1 + eintraege.length) % eintraege.length;
        else if (e.key === 'Home') ziel = 0;
        else if (e.key === 'End') ziel = eintraege.length - 1;
        if (ziel === null) return;
        e.preventDefault();
        knoepfe.current[ziel]?.focus();
        wechsle(eintraege[ziel].view);
    };

    return (
        <div data-glass-view="bereichsumschalter" className="shrink-0 px-4 md:px-6 pt-4">
            <div
                data-glass="umschalter"
                role="tablist"
                aria-label="Ansicht wählen"
                className="inline-flex flex-wrap rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm"
            >
                {eintraege.map((e, i) => {
                    const istAktiv = e.view === aktiv;
                    return (
                        <button
                            key={e.view}
                            ref={el => { knoepfe.current[i] = el; }}
                            type="button"
                            role="tab"
                            aria-selected={istAktiv}
                            tabIndex={i === tabStopp ? 0 : -1}
                            onClick={() => wechsle(e.view)}
                            onKeyDown={ev => onKeyDown(ev, i)}
                            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                                istAktiv
                                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                                    : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {e.icon}
                            {e.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};
